import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { useToggleable as useDrawer } from '@stores/toggleables';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import Item, { isRecordedSession } from '@models/Item';
import { getLessonMetrics, fetchLessonFeedback } from '@actions/educator';
import { fetchQuizLeaderBoard } from '@actions/quizTaking';
import MoreVert from '@icon/MoreVert';
import Play from '@icon/Play';
import pushRoute from '@utils/pushRoute';
import LeftIcon from '@cont/Course/LeftIcon';
import StatusButton from '@cont/Course/StatusButton';
import { commas, getCommaSeparatedUIDs } from '@utils/textHelper';
import { Spacings } from '@styles/index';
import { DRAWER_IDS, sessionStates } from '@constants/index';
import useEducatorContext from '@hooks/useEducatorContext';
import {
  EDUCATOR_COURSE_PAGE,
  EDUCATOR_PRACTICE_ADD_PRACTICE_CLICKED,
  EDUCATOR_PRACTICE_REVIEW_DRAFT_CLICKED
} from '@constants/segment';
import ClassMetaData from '../TemplateSchedule/ClassMetaData';
import Statistics from '../AutoDPP/Statistics';
import useDPPEvents from '../AutoDPP/useDPPEvents';

const CourseInfo = styled(P2)`
  margin-top: ${Spacings.SPACING_2B};
`;

const ItemInfo = styled.div`
  width: 365px;
  margin-right: ${Spacings.SPACING_12B};
`;

const MenuButton = styled.div`
  display: flex;
  height: 22px;
`;

const MenuWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${Spacings.SPACING_4B};
  border: 1px solid var(--color-i-gray-v2);
  border-radius: 3px;
  padding: ${Spacings.SPACING_2B};
  &:hover {
    background-color: var(--color-i-gray-v2);
  }
`;

const StatsDiv = styled.div`
  display: flex;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const Wrapper = styled.div`
  padding: ${Spacings.SPACING_6B};
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_6B};
  box-shadow: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_8B} var(--color-shadow);
  display: flex;
  flex-direction: column;
  grid-column: 9/25;
  justify-content: space-between;
  cursor: pointer;
  :hover {
    box-shadow: 0 ${Spacings.SPACING_1B} ${Spacings.SPACING_2B}
      var(--color-shadow);
  }
  transition: box-shadow 0.2s ease-in-out;
`;

const ClassItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ClassMeta = styled(ClassMetaData)`
  margin-left: ${Spacings.SPACING_16B};
`;

const AutoDppButton = styled(Button)`
  border-radius: 4px;
  font-size: 14px;
  height: 40px;
`;

const AutoDppStatsWrapper = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_4B};
  padding-top: ${Spacings.SPACING_6B};
  border-top: solid 1px;
  border-top-color: var(--color-i-gray-v2);
`;

const liveButtonClick = (classUID, openDrawer) => (e) => {
  e.stopPropagation();
  openDrawer();
  pushRoute('/educator/live/[classUID]', `/educator/live/${classUID}`);
};

const ItemCard = ({
  lesson,
  onItemClick,
  isTestSeries,
  setAnchorEl,
  setLessonDrawer,
  me,
  courseType
}) => {
  const router = useRouter();
  const dispatch = useDispatchToStore();
  const trackEventsOnSegment = useDPPEvents();
  const isEducatorSide = useEducatorContext();
  const {
    drive_details: educatorSlides,
    learner_notes: learnerNotes,
    practice_quiz: practiceQuiz
  } = lesson.liveClassDetails || {};
  const { uid: meUID, isModerator, isEducator } = me;
  const { author } = lesson;
  const { uid: authorUID } = author;

  const isRecordedClass = isRecordedSession({
    courseType,
    classType: lesson.classType
  });
  const isLesssonLive = Item.isLessonLive(lesson, 3600 * 1000);

  const isLessonUpcoming = Item.isUpcoming(lesson);

  const showEditClassMenu =
    !isRecordedClass && !isLesssonLive && meUID === authorUID;

  const showDPPButton =
    !isRecordedClass &&
    !isLesssonLive &&
    lesson.practiceDpp?.cta &&
    meUID === authorUID;

  const showCreateQuizButton =
    !isRecordedClass &&
    !isLesssonLive &&
    isLessonUpcoming &&
    meUID === authorUID;

  const onLessonClick = () => {
    if (
      isRecordedClass ||
      lesson.state === sessionStates.FUTURE ||
      (!isModerator && authorUID !== meUID)
    )
      return;
    onItemClick(lesson);
    Promise.all([
      dispatch(getLessonMetrics(lesson.uid, lesson.type)),
      dispatch(fetchLessonFeedback(lesson.uid, lesson.type))
    ]);
    if (lesson.type === 'quiz')
      dispatch(
        fetchQuizLeaderBoard({
          quizUID: lesson.uid,
          isLive: lesson.isLive,
          nextPage: false
        })
      );
    setLessonDrawer(true);
  };

  const handleMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget, lesson);
  };

  const infoString = Item.getInfoString(
    lesson,
    isTestSeries,
    true,
    false,
    isEducatorSide,
    true,
    true,
    courseType
  );

  let { totalWatchTime, likePercentage, type: lessonType } = lesson;

  const practiceDppStatistics = lesson?.practiceDpp?.statistics;

  if (totalWatchTime) totalWatchTime = commas(Math.floor(totalWatchTime / 60));
  let isLessonQuiz = true;
  if (lessonType === 'post') isLessonQuiz = false;

  const isDPPNotAvailable = !lesson?.practiceDpp;

  const routeToDpp = (event, uid = lesson?.uid) => {
    event.stopPropagation();
    if (uid) {
      window.open(`/educator/auto-dpp/${uid}`);
    }
  };

  const onAutoDppButtonClick = (event) => {
    if (lesson.practiceDpp?.cta === 'Add Practice') {
      trackEventsOnSegment(EDUCATOR_PRACTICE_ADD_PRACTICE_CLICKED, {
        last_primary_source: EDUCATOR_COURSE_PAGE,
        goal_name: lesson?.goalName ?? 'NA',
        goal_uid: lesson?.goalUid ?? 'NA'
      });
    }
    if (lesson.practiceDpp?.cta === 'Review Draft') {
      trackEventsOnSegment(EDUCATOR_PRACTICE_REVIEW_DRAFT_CLICKED, {
        last_primary_source: EDUCATOR_COURSE_PAGE,
        goal_name: lesson?.goalName ?? 'NA',
        goal_uid: lesson?.goalUid ?? 'NA'
      });
    }
    return routeToDpp(event, lesson?.uid);
  };

  const isStatisticsShown = () => {
    if (isRecordedClass) return false;
    return (
      (!isLessonQuiz && !isLessonUpcoming && !isLesssonLive) ||
      lesson?.practiceDpp?.statistics
    );
  };

  const getPracticeAttemptsWithComma = () => {
    if (practiceDppStatistics.practice_attempts) {
      return commas(practiceDppStatistics.practice_attempts);
    }
    return 0;
  };

  const getAccuracy = () => {
    if (practiceDppStatistics.accuracy) {
      return Math.round(practiceDppStatistics.accuracy);
    }
    return 0;
  };

  const { openElement: openDrawer } = useDrawer(DRAWER_IDS.COURSE_CLASS_PLAYER);

  const isMyLessonLive = isLesssonLive && me?.uid === lesson.author.uid;

  const getAutoDppStatistics = () => {
    return (
      <>
        <Statistics
          data={{
            code: 'PRACTICE_ATTEMPTS',
            value: getPracticeAttemptsWithComma()
          }}
        />
        <Statistics
          data={{
            code: 'ACCURACY',
            value: `${getAccuracy()}%`
          }}
        />
      </>
    );
  };

  return (
    <Wrapper onClick={onLessonClick}>
      <ClassItemWrapper>
        <Flex>
          <LeftIcon lesson={lesson} />
          <ItemInfo id={lesson.uid}>
            <H6>{lesson.name}</H6>
            <CourseInfo color="var(--color-text-secondary)">
              {infoString}
            </CourseInfo>
          </ItemInfo>
        </Flex>
        <Flex>
          {!isRecordedClass && (
            <StatsDiv>
              {isMyLessonLive && (
                <Button
                  label="Go live"
                  color="red"
                  grid={3}
                  onClick={liveButtonClick(lesson.uid, openDrawer)}
                  icon={
                    <Play
                      size={Spacings.SPACING_4B}
                      color="var(--color-base-1)"
                    />
                  }
                />
              )}
            </StatsDiv>
          )}
          {showCreateQuizButton && (
            <StatusButton
              sourceURL={router.asPath}
              isTestSeries={isTestSeries}
              contentType={lesson.creationType}
              contentState={lesson.contentState}
              sessionUID={lesson.uid}
            />
          )}
          {showDPPButton && (
            <AutoDppButton
              color="black"
              label={lesson.practiceDpp.cta}
              width="144px"
              maxHeight="sm"
              onClick={onAutoDppButtonClick}
            />
          )}
          {showEditClassMenu && (
            <MenuWrapper onClick={handleMenuClick} className="menuButton">
              <MenuButton>
                <MoreVert size="22px" color="var(--color-text-primary)" />
              </MenuButton>
            </MenuWrapper>
          )}
        </Flex>
      </ClassItemWrapper>
      {isStatisticsShown() && (
        <AutoDppStatsWrapper>
          {!isLessonQuiz && !isLessonUpcoming && !isLesssonLive && (
            <>
              <Statistics
                data={{ code: 'MINS', value: `${totalWatchTime || 0}` }}
              />
              <Statistics
                data={{ code: 'LIKED', value: `${likePercentage || 0}%` }}
              />
            </>
          )}
          {practiceDppStatistics && getAutoDppStatistics()}
        </AutoDppStatsWrapper>
      )}
      <ClassMeta
        isEducator={isEducator}
        learnerNotes={learnerNotes}
        educatorSlides={educatorSlides}
        quizUID={isDPPNotAvailable && practiceQuiz?.uid}
        canView
        index={0}
        goalUID={lesson.goalUid}
        topicGroups={getCommaSeparatedUIDs(lesson.topicGroups)}
      />
    </Wrapper>
  );
};

export default ItemCard;

ItemCard.propTypes = {
  lesson: PropTypes.objectOf(PropTypes.any).isRequired,
  onItemClick: PropTypes.func.isRequired,
  isTestSeries: PropTypes.bool,
  setAnchorEl: PropTypes.func.isRequired,
  setLessonDrawer: PropTypes.func.isRequired,
  me: PropTypes.objectOf(PropTypes.any),
  courseType: PropTypes.number
};

ItemCard.defaultProps = {
  isTestSeries: false,
  courseType: 1,
  me: {}
};
